import { useAuthQuery } from '@/api/index.ts';
import { orgQueryKeys } from '@/config/queryKeys';
import { createOrganization, listOrganizations } from '@/services/organizations';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { useForm } from '@tanstack/react-form';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useStore } from '@tanstack/react-store';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { OrgFormApi } from './formTypes';
import type { OrgFormValues } from './schema';
import { createOrgSchema } from './schema';

export function useCreateOrganization(): { form: OrgFormApi; hasAttemptedSubmit: boolean } {
  const { t } = useTranslation('orgs');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // useAuthQuery over useAuthStatus: same /auth/me cache, minus the redirect effect. isError is not
  // redundant — a failed refetch keeps stale `data`, which useAuthStatus reports as UNAUTHENTICATED.
  const { data: authUser, isPending: isAuthPending, isError: isAuthError } = useAuthQuery();
  const isAuthenticated = !isAuthPending && !isAuthError && authUser !== null && authUser !== undefined;

  // Client-side uniqueness check only — this cache can be stale relative to a concurrent
  // creation, so the real guarantee still needs a backend uniqueness constraint (POR-395).
  const { data: existingOrgs } = useQuery({
    queryKey: orgQueryKeys.list(),
    queryFn: listOrganizations,
    // A cold deep-link mounts this before /auth/me resolves; ungated, GET /orgs races its own 401
    // into the global login redirect (POR-442).
    enabled: isAuthenticated,
  });
  const existingAliases = useMemo(
    () =>
      new Set(
        (existingOrgs ?? []).map(org => org.alias?.trim().toLowerCase()).filter((alias): alias is string => !!alias),
      ),
    [existingOrgs],
  );

  const schema = useMemo(() => createOrgSchema(t, existingAliases), [t, existingAliases]);

  const form = useForm({
    formId: 'organizationCreate',
    defaultValues: {
      name: '',
      address: '',
      alias: '',
      primaryContact: {
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
      },
      itContact: {
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
      },
      domain: '',
      subscription: {
        active: true,
        expiration: '',
      },
    } as OrgFormValues,
    validators: {
      onMount: schema,
      onChange: schema,
    },
    onSubmit: async ({ value }) => {
      try {
        // Map OrgFormValues to API OrgCreate shape. `phone` is optional/nullable on the backend
        // but rejects an empty string as a malformed number, so a blank field maps to `null`.
        // Required text fields are trimmed here too, since validation trims before checking and
        // an untrimmed value could otherwise pass client-side and reach the backend padded.
        const payload = {
          name: value.name.trim(),
          email_domain: value.domain.trim(),
          alias: value.alias.trim(),
          address: value.address.trim(),
          primary_contact: {
            first_name: value.primaryContact.firstName.trim(),
            last_name: value.primaryContact.lastName.trim(),
            phone: value.primaryContact.phone.trim() || null,
            email: value.primaryContact.email.trim(),
          },
          it_contact: {
            first_name: value.itContact.firstName.trim(),
            last_name: value.itContact.lastName.trim(),
            phone: value.itContact.phone.trim() || null,
            email: value.itContact.email.trim(),
          },
          subscription: {
            is_active: value.subscription.active,
            expires_at: value.subscription.expiration,
          },
        };
        await createOrganization(payload);
        toast.success(t('create_org_success'));
        await queryClient.invalidateQueries({
          queryKey: orgQueryKeys.list(),
        });
        form.reset();
        await navigate({ to: '/organizations' });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : undefined;
        toast.error(errorMessage ? t('create_org_error_detail', { message: errorMessage }) : t('create_org_error'));
      }
    },
    onSubmitInvalid: () => {
      requestAnimationFrame(() => {
        const firstInvalid = document.querySelector('[aria-invalid="true"]') as HTMLElement | null;
        firstInvalid?.focus();
      });
    },
  });

  const hasAttemptedSubmit = useStore(form.baseStore, s => s.submissionAttempts > 0);

  return { form: form as unknown as OrgFormApi, hasAttemptedSubmit };
}
